import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Calendar, Settings, X, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCountdownTimer = ({ showAdminButton = true }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [targetDate, setTargetDate] = useState(null);
  const [timerMessage, setTimerMessage] = useState('Registration Closes In');
  
  // Admin states
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [newTargetDate, setNewTargetDate] = useState('');
  const [newTargetTime, setNewTargetTime] = useState('23:59');
  const [newTimerMessage, setNewTimerMessage] = useState('Registration Closes In');
  const [saving, setSaving] = useState(false);

  // Load countdown settings from backend
  useEffect(() => {
    const fetchCountdown = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/countdown`);
        if (response.ok) {
          const data = await response.json();
          if (data.target_date) {
            setTargetDate(new Date(data.target_date));
            if (data.message) {
              setTimerMessage(data.message);
              setNewTimerMessage(data.message);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching countdown:', error);
      }
    };
    fetchCountdown();
  }, []);

  // Countdown timer logic
  useEffect(() => {
    if (!targetDate) return;

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const target = new Date(targetDate).getTime();
      const difference = target - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000)
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  // Handle admin password verification
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setShowPasswordPrompt(false);
    setShowAdminPanel(true);
    setPasswordError('');
  };

  // Save countdown settings to backend
  const handleSaveCountdown = async () => {
    if (!newTargetDate) {
      toast.error('Please select an end date');
      return;
    }

    setSaving(true);
    try {
      const dateTime = new Date(`${newTargetDate}T${newTargetTime}`);
      
      const response = await fetch(`${BACKEND_URL}/api/countdown`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          password: adminPassword || 'Laikos2024',
          target_date: dateTime.toISOString(),
          message: newTimerMessage
        })
      });

      if (response.ok) {
        setTargetDate(dateTime);
        setTimerMessage(newTimerMessage);
        setShowAdminPanel(false);
        setAdminPassword('');
        toast.success('Countdown timer updated successfully!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save countdown');
      }
    } catch (error) {
      console.error('Error saving countdown:', error);
      toast.error('Failed to save countdown timer');
    } finally {
      setSaving(false);
    }
  };

  const isExpired = timeLeft.days === 0 && timeLeft.hours === 0 && timeLeft.minutes === 0 && timeLeft.seconds === 0 && targetDate && new Date() > targetDate;
  const hasNoTimer = !targetDate;

  return (
    <>
      <section className="bg-gradient-to-r from-orange-500 to-red-500 py-12 px-4 relative">
        {/* Admin Settings Button */}
        {showAdminButton && (
          <button
            onClick={() => setShowPasswordPrompt(true)}
            className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
            title="Admin: Set Countdown"
            data-testid="countdown-admin-btn"
          >
            <Settings size={20} className="text-white" />
          </button>
        )}

        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Clock size={28} className="text-white" />
            <h2 className="text-2xl font-bold text-white">{timerMessage}</h2>
          </div>
          
          {hasNoTimer ? (
            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-6">
              <p className="text-2xl font-bold text-white">Registration Timer Not Set</p>
              <p className="text-white/80 mt-2">Admin: Click the settings icon to configure the countdown timer.</p>
              <Link to="/register">
                <Button className="mt-4 bg-white text-orange-500 hover:bg-gray-100 font-semibold px-8 py-3 text-lg">
                  Register Now
                </Button>
              </Link>
            </div>
          ) : isExpired ? (
            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-6">
              <p className="text-2xl font-bold text-white">Registration Has Closed</p>
              <p className="text-white/80 mt-2">Please contact us for more information about upcoming registration periods.</p>
            </div>
          ) : (
            <>
              <div className="flex justify-center gap-4 md:gap-8 mb-6">
                {/* Days */}
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 md:p-6 min-w-[80px] md:min-w-[120px]">
                  <div className="text-4xl md:text-6xl font-bold text-white">
                    {String(timeLeft.days).padStart(2, '0')}
                  </div>
                  <div className="text-white/80 text-sm md:text-base uppercase tracking-wider mt-2">Days</div>
                </div>
                
                {/* Hours */}
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 md:p-6 min-w-[80px] md:min-w-[120px]">
                  <div className="text-4xl md:text-6xl font-bold text-white">
                    {String(timeLeft.hours).padStart(2, '0')}
                  </div>
                  <div className="text-white/80 text-sm md:text-base uppercase tracking-wider mt-2">Hours</div>
                </div>
                
                {/* Minutes */}
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 md:p-6 min-w-[80px] md:min-w-[120px]">
                  <div className="text-4xl md:text-6xl font-bold text-white">
                    {String(timeLeft.minutes).padStart(2, '0')}
                  </div>
                  <div className="text-white/80 text-sm md:text-base uppercase tracking-wider mt-2">Minutes</div>
                </div>
                
                {/* Seconds */}
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 md:p-6 min-w-[80px] md:min-w-[120px]">
                  <div className="text-4xl md:text-6xl font-bold text-white">
                    {String(timeLeft.seconds).padStart(2, '0')}
                  </div>
                  <div className="text-white/80 text-sm md:text-base uppercase tracking-wider mt-2">Seconds</div>
                </div>
              </div>

              {targetDate && (
                <p className="text-white/80 flex items-center justify-center gap-2">
                  <Calendar size={16} />
                  Registration closes on {targetDate.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              )}

              <Link to="/register">
                <Button className="mt-6 bg-white text-orange-500 hover:bg-gray-100 font-semibold px-8 py-3 text-lg">
                  Register Now
                </Button>
              </Link>
            </>
          )}
        </div>
      </section>

      {/* Admin Password Prompt Modal */}
      {showPasswordPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <Lock size={20} />
                Admin Access
              </h3>
              <button 
                onClick={() => {
                  setShowPasswordPrompt(false);
                  setAdminPassword('');
                  setPasswordError('');
                }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <Label htmlFor="adminPassword">Enter Admin Password</Label>
                <Input
                  id="adminPassword"
                  type="password"
                  value={adminPassword}
                  onChange={(e) => {
                    setAdminPassword(e.target.value);
                    setPasswordError('');
                  }}
                  placeholder="Enter password"
                  className="mt-1"
                />
                {passwordError && (
                  <p className="text-red-500 text-sm mt-1">{passwordError}</p>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowPasswordPrompt(false);
                    setAdminPassword('');
                    setPasswordError('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#5f7c8a] hover:bg-[#4a6470]"
                >
                  Access Settings
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Admin Panel Modal */}
      {showAdminPanel && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <Settings size={20} />
                Set Registration Countdown
              </h3>
              <button 
                onClick={() => setShowAdminPanel(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="adminPwd">Admin Password</Label>
                <Input
                  id="adminPwd"
                  type="password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  placeholder="Enter admin password"
                />
              </div>

              <div>
                <Label htmlFor="timerMessage">Timer Message</Label>
                <Input
                  id="timerMessage"
                  value={newTimerMessage}
                  onChange={(e) => setNewTimerMessage(e.target.value)}
                  placeholder="Registration Closes In"
                />
              </div>

              <div>
                <Label htmlFor="targetDate">End Date</Label>
                <Input
                  id="targetDate"
                  type="date"
                  value={newTargetDate}
                  onChange={(e) => setNewTargetDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div>
                <Label htmlFor="targetTime">End Time</Label>
                <Input
                  id="targetTime"
                  type="time"
                  value={newTargetTime}
                  onChange={(e) => setNewTargetTime(e.target.value)}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowAdminPanel(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-[#5f7c8a] hover:bg-[#4a6470]"
                  onClick={handleSaveCountdown}
                  disabled={!newTargetDate || !adminPassword || saving}
                >
                  {saving ? 'Saving...' : 'Save Countdown'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminCountdownTimer;
